import {
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  Heading,
  IconButton,
  Text,
  useToast,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useContext, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { Card, CardBody } from '../components/Card'
import { FormLabel } from '../components/FormLabel'
import { InputGroup } from '../components/InputGroup'
import { TextareaGroup } from '../components/TextareaGroup'
import { useAuth } from '../context/AuthContext'
import { useApp } from '../hooks/useApp'
import { sendHelpdeskEmail } from '../hooks/useDocuments'
import { Trash } from '../icons'
import { insuranceParamsContext } from '../layouts/Wizard'

interface HelpDeskFormValues {
  name: string
  email: string
  phone: string
  subject: string
  questionType: string
  message: string
  files?: FileList | null
}

const schema = yup
  .object()
  .shape({
    name: yup.string().required('Zadejte jméno a příjmení'),
    email: yup.string().required('Zadejte email'),
    subject: yup.string().required('Zadejte předmět'),
    message: yup.string().required('Napište zprávu'),
  })
  .required()

export const HelpDesk = ({
  isOpen,
  onOpen,
  onClose,
}: {
  isOpen: boolean
  onOpen: Function
  onClose: Function
}) => {
  const { user } = useAuth() ?? {}

  const { product } = useApp()

  const { insuranceParamsId } = useContext(insuranceParamsContext)

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<HelpDeskFormValues>({
    mode: 'onBlur',
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: user?.userFullName,
      email: user?.email,
      phone: user?.phoneNumber,
    },
  })

  useEffect(() => {
    reset({
      name: user?.userFullName,
      email: user?.email,
      phone: user?.phoneNumber,
      subject: insuranceParamsId ? 'Nabídka ' + insuranceParamsId : '',
    })
  }, [user, insuranceParamsId, reset])

  const toast = useToast()
  const files = watch('files')
  const filesArray = Array.from(files || [])

  const onSubmit = (values: HelpDeskFormValues) => {
    sendHelpdeskEmail({
      name: values.name,
      email: values.email,
      phone: values.phone,
      subject: values.subject,
      message: values.message,
      product: product ?? undefined,
    })
      .then((resp) => {
        if (resp.data.success) {
          toast({
            title: 'Zpráva odeslána!',
            description: 'Vaše zpráva HelpDesku byla úspěšně odeslána.',
            status: 'success',
            duration: 5000,
            isClosable: true,
          })
          reset() // reset form
          onClose() // close helpdesk
        } else {
          toast({
            title: 'Zprávu se nepodařilo odeslat!',
            description: resp.data.text,
            status: 'error',
            duration: 5000,
            isClosable: true,
          })
        }
      })
      .catch(() => {
        toast({
          title: 'Zprávu se nepodařilo odeslat!',
          description: 'Interní chyba odeslání zprávy',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      })

    // return new Promise((resolve) => {
    //   setTimeout(() => {
    //     console.log(values)
    //     resolve(true)
    //   }, 1000)
    // }).then(() => {
    //   reset() // reset form
    //   onClose() // close helpdesk
    //   toast({
    //     title: 'Zpráva odeslána!',
    //     description: 'Vaše zpráva HelpDesku byla úspěšně odeslána.',
    //     status: 'success',
    //     duration: 5000,
    //     isClosable: true,
    //   })
    // })
  }

  return (
    <Card
      sx={{
        display: 'grid',
        position: 'fixed',
        top: 'auto',
        height: '100%',
        maxH: 'calc(100vh - 12rem)',
        right: 4,
        bottom: { base: 40, xl: 24 },
        transform: isOpen ? 'translateX(0)' : 'translateX(200%)',
        transitionProperty: 'all',
        transitionDuration: 'fast',
        boxShadow: '3xl',
        overflow: 'hidden',
        zIndex: 1200,
      }}
    >
      <CardBody sx={{ height: '100%', overflow: 'auto' }}>
        <Flex
          as="header"
          sx={{ mb: 4, alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Heading as="h2" sx={{ fontSize: 'lg' }}>
            HelpDesk
          </Heading>

          <CloseButton onClick={() => onClose()} />
        </Flex>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: 'grid', gap: 4 }}>
            <FormControl isInvalid={!!errors.name}>
              <FormLabel htmlFor="name">Jméno a příjmení</FormLabel>

              <InputGroup
                type="text"
                inputMode="text"
                autoComplete="name"
                placeholder="Vaše jméno a příjmení"
                errors={errors.name}
                {...register('name')}
              />
            </FormControl>

            <FormControl isInvalid={!!errors.email}>
              <FormLabel htmlFor="email">E-mail</FormLabel>

              <InputGroup
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Váš e-mail"
                errors={errors.email}
                {...register('email')}
              />
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="phone">Telefon</FormLabel>

              <InputGroup
                type="tel"
                inputMode="tel"
                autoComplete="phone"
                placeholder="Vaše telefonní číslo"
                {...register('phone')}
              />
            </FormControl>

            <FormControl isInvalid={!!errors.subject}>
              <FormLabel htmlFor="subject">Předmět</FormLabel>

              <InputGroup
                type="text"
                inputMode="text"
                autoComplete="off"
                placeholder="Např. Číslo nabídky"
                errors={errors.subject}
                {...register('subject')}
              />
            </FormControl>

            {/* <FormControl variant="fullWidth">
              <FormLabel>Typ dotazu</FormLabel>

              <Select {...register('questionType')} sx={{ w: '100%' }}>
                <option disabled value="">
                  Vyberte typ dotazu
                </option>
                <option value="general">Obecný dotaz</option>
              </Select>
            </FormControl> */}

            <FormControl variant="fullWidth" isInvalid={!!errors.message}>
              <FormLabel htmlFor="message">Zpráva</FormLabel>

              <TextareaGroup
                autoComplete="off"
                placeholder="Napište text Vaší zprávy"
                rows={4}
                errors={errors.message}
                {...register('message')}
              />
            </FormControl>

            {/* <FormControl>
              <FormLabel htmlFor="files">Přílohy</FormLabel>

              <FileInput
                id="files"
                {...register('files')}
                multiple
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setValue('files', event.target.files || undefined)
                }}
              />
            </FormControl> */}

            {files && (
              <Box>
                {filesArray.map((file) => (
                  <Flex
                    key={file.name}
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 4,
                    }}
                  >
                    <Text
                      sx={{
                        maxW: '240px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={`${String(Math.round(file.size / 1024))} kB`}
                    >
                      {file.name}
                    </Text>

                    <IconButton
                      colorScheme="white"
                      aria-label="Odebrat soubor"
                      onClick={() => setValue('files', null)}
                    >
                      <Trash color="red.500" width={4} height={4} />
                    </IconButton>
                  </Flex>
                ))}
              </Box>
            )}

            <Button
              colorScheme="primary"
              type="submit"
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
            >
              Odeslat zprávu
            </Button>
          </Box>
        </form>
      </CardBody>
    </Card>
  )
}
